import { isProductAnalyticsClientEvent, isProductAnalyticsProperties, normalizeProductAnalyticsRoute, } from "@fluenceur/types";
const ROUTE_FEATURES = {
    login: "authentication",
    signup: "authentication",
    verify: "authentication",
    onboarding: "onboarding",
    checkout: "payments",
    payout: "payments",
    collabs: "collabs",
    c: "collabs",
    rights: "rights",
    opportunities: "applications",
    wallet: "withdrawals",
    profile: "profile",
    referrals: "referrals",
    performance: "performance",
    messages: "conversations",
    conversations: "conversations",
    gifts: "gifts",
    gifting: "gifts",
    discovery: "discovery",
    recommendations: "recommendations",
    favorites: "favorites",
    creators: "creators",
    contacts: "contacts",
    programs: "programs",
    invitations: "invitations",
    applications: "applications",
    affiliates: "programs",
    briefs: "briefs",
    contents: "contents",
    mentions: "contents",
    orders: "orders",
    commissions: "commissions",
    funding: "funding",
    payments: "payments",
    withdrawals: "withdrawals",
    documents: "documents",
    flows: "flows",
    companion: "companion",
    assistants: "assistants",
    mcp: "assistants",
    "brand-space": "brand_space",
    studio: "studio",
    settings: "settings",
    team: "team",
    imports: "imports",
    exports: "exports",
};
function routeFeature(route) {
    if (["/", "/forgot-password", "/reset-password"].includes(route))
        return "authentication";
    if (route === "/affiliates/invitations")
        return "invitations";
    if (route === "/creators/discovery")
        return "discovery";
    if (route.startsWith("/collabs/brief-templates"))
        return "briefs";
    if (route.startsWith("/settings/integrations"))
        return "integrations";
    if (route === "/settings/billing")
        return "plans";
    if (route === "/settings/invoices" || route === "/wallet/documents")
        return "documents";
    if (route === "/settings/team")
        return "team";
    if (route.includes("/notifications"))
        return "notifications";
    if (route === "/settings/account/assistants")
        return "assistants";
    if (route === "/settings/agents" || route === "/onboarding/companion")
        return "companion";
    if (route === "/settings/brand-dna")
        return "brand_dna";
    if (route === "/payments/commissions")
        return "commissions";
    if (route === "/brand-space/studio")
        return "studio";
    return ROUTE_FEATURES[route.split("/")[1]];
}
/** A small explicit-event adapter: no SDK collection, persistence, identity, or DOM inspection. */
export class ProductAnalyticsClient {
    constructor(transport) {
        this.transport = transport;
        this.enabled = false;
        this.consentEpoch = 0;
        this.previousPath = null;
    }
    setEnabled(enabled) {
        if (this.enabled !== enabled) {
            this.previousPath = null;
            this.consentEpoch += 1;
        }
        this.enabled = enabled;
    }
    /** Bind deferred outcomes to the consent/session in which their action began. */
    captureForCurrentConsent() {
        const eligibleAtStart = this.enabled;
        const epoch = this.consentEpoch;
        return (event, properties = {}) => {
            if (eligibleAtStart && epoch === this.consentEpoch)
                this.capture(event, properties);
        };
    }
    capture(event, properties = {}) {
        if (!this.enabled ||
            !isProductAnalyticsClientEvent(event) ||
            !isProductAnalyticsProperties(properties))
            return;
        // Never allow analytics failures to escape into an action or rendering path.
        try {
            void this.transport.send(event, { ...properties }).catch(() => { });
        }
        catch {
            // Transports can throw synchronously as well as reject.
        }
    }
    screen(pathname, properties = {}) {
        if (!this.enabled)
            return;
        // The unexported path is used only to distinguish navigation between two records.
        // Query and fragment changes never count as another screen.
        const path = pathname.split(/[?#]/, 1)[0];
        if (path === this.previousPath)
            return;
        this.previousPath = path;
        const route = normalizeProductAnalyticsRoute(path);
        const feature = properties.feature ?? routeFeature(route);
        this.capture("screen_viewed", {
            ...properties,
            ...(feature ? { feature } : {}),
            route,
        });
    }
    reset() {
        this.consentEpoch += 1;
        this.enabled = false;
        this.previousPath = null;
    }
}
